import CasinoIcon from '@mui/icons-material/Casino';
import ChatBubbleIcon from '@mui/icons-material/ChatBubble';
import CommentIcon from '@mui/icons-material/Comment';
import EuroSymbolIcon from '@mui/icons-material/EuroSymbol';
import StarIcon from '@mui/icons-material/Star';
import WatchLaterIcon from '@mui/icons-material/WatchLater';
import Badge from '@mui/material/Badge';
import {lazy, Suspense, useCallback, useEffect, useState, type ReactNode} from 'react';
import {Navigate, Route, Routes} from 'react-router';
import {getEventDay} from '../api/eventDayApi';
import {getPrizes} from '../api/prizeApi';
import {getAllSessionsOfToday} from '../api/sessionApi';
import {getVouchers} from '../api/voucherApi';
import {getOwnWins} from '../api/winApi';
import CenteredCircularProgress from '../components/CenteredCircularProgress';
import type {EventDay} from '../entities/EventDay';
import type {Prize} from '../entities/Prize';
import type {Session} from '../entities/Session';
import type {Win} from '../entities/Win';
import PageWithNavigation, {type DrawerEntry} from '../navigation/PageWithNavigation';
import {session} from '../security/keycloak';
import {getCookie, setCookie} from '../util/cookieUtils';
import {isNotFound} from '../util/httpErrors';
import {
  AGB_ROUTE,
  CONTEST_PARTICIPATION_ROUTE,
  CONTEST_ROUTE,
  GREETING_ROUTE,
  IMPRINT_ROUTE,
  MY_WINS_ROUTE,
  NEWS_ROUTE,
  PRIVACY_POLICY_ROUTE,
  PROFILE_ROUTE,
  REGISTRATION_ROUTE,
  SESSION_ROUTE,
  VOUCHER_ROUTE,
} from './routes';

// Lazy imports for code splitting
const NewsPage = lazy(() => import('../pages/news/NewsPage'));
const GreetingPage = lazy(() => import('../pages/greeting/GreetingPage'));
const VoucherPage = lazy(() => import('../pages/voucher/VoucherPage'));
const SessionPage = lazy(() => import('../pages/session/SessionPage'));
const ContestPage = lazy(() => import('../pages/contest/ContestPage'));
const ContestParticipationPage = lazy(() => import('../pages/contest/ContestParticipationPage'));
const NotFoundPage = lazy(() => import('../pages/404/NotFoundPage'));
const RegistrationPage = lazy(() => import('../pages/registration/RegistrationPage'));
const ProfilePage = lazy(() => import('../pages/profile/ProfilePage'));
const MyWinsPage = lazy(() => import('../pages/wins/MyWinsPage'));
const PrivacyPolicyPage = lazy(() => import('../pages/legal/PrivacyPolicyPage'));
const ImprintPage = lazy(() => import('../pages/legal/ImprintPage'));
const AgbPage = lazy(() => import('../pages/legal/AgbPage'));

const LAST_VIEWED_CONTEST_DAY_COOKIE = 'last_viewed_contest_day';
const LAST_VIEWED_SESSION_IDS_COOKIE = 'last_viewed_session_ids';

/** Data that decides which menu entries and badges are shown. */
interface MenuState {
  wins: Win[];
  today?: EventDay;
  prizes: Prize[];
  sessionsOfToday: Session[];
  hasVouchers: boolean;
}

function readLastViewedSessionIds(): number[] {
  try {
    const parsed: unknown = JSON.parse(getCookie(LAST_VIEWED_SESSION_IDS_COOKIE) || '[]');
    return Array.isArray(parsed) ? parsed.filter((id): id is number => typeof id === 'number') : [];
  } catch {
    return [];
  }
}

/** Runs `onViewed` once when the page is shown, e.g. to clear a badge. */
function Viewed({onViewed, children}: {onViewed(): void; children: ReactNode}) {
  useEffect(() => {
    onViewed();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return children;
}

export default function MyRouter() {
  const [menuState, setMenuState] = useState<MenuState>();
  const [lastViewedContestDay, setLastViewedContestDay] = useState(
    () => parseInt(getCookie(LAST_VIEWED_CONTEST_DAY_COOKIE) || '-1', 10),
  );
  const [lastViewedSessionIds, setLastViewedSessionIds] = useState<number[]>(readLastViewedSessionIds);

  const updateWins = useCallback(async (): Promise<Win[]> => {
    const wins = session.currentUser ? await getOwnWins() : [];
    setMenuState(previous => (previous ? {...previous, wins} : previous));
    return wins;
  }, []);

  useEffect(() => {
    (async () => {
      const state: MenuState = {wins: [], prizes: [], sessionsOfToday: [], hasVouchers: false};
      try {
        state.wins = session.currentUser ? await getOwnWins() : [];
        state.hasVouchers = (await getVouchers({page: 0, size: 1})).totalElements > 0;
        state.today = await getEventDay('today');
        state.prizes = await getPrizes(state.today.id.toString());
        state.sessionsOfToday = await getAllSessionsOfToday();
      } catch (error) {
        // 404: there is no event day today (and therefore no prizes or sessions of today)
        if (!isNotFound(error)) {
          throw error;
        }
      }
      setMenuState(state);
    })();
  }, []);

  if (!menuState) {
    return <CenteredCircularProgress/>;
  }

  const {wins, today, prizes, sessionsOfToday, hasVouchers} = menuState;
  const contestIsNew = prizes.length > 0 && today !== undefined && lastViewedContestDay !== today.id;
  const unviewedSessionCount = sessionsOfToday.filter(s => !lastViewedSessionIds.includes(s.id)).length;
  const unclaimedWins = wins.filter(win => !win.claimed);

  const withBadge = (icon: ReactNode, count: number | string) => (
    <Badge color="secondary" badgeContent={count}>{icon}</Badge>
  );
  const drawerEntries: DrawerEntry[] = [
    {name: 'Neuigkeiten', link: NEWS_ROUTE, icon: <CommentIcon/>},
    {name: 'Gewinnspiel', link: CONTEST_ROUTE, icon: contestIsNew ? withBadge(<CasinoIcon/>, '1') : <CasinoIcon/>},
  ];
  // Show the vouchers entry only if there is at least one voucher
  if (hasVouchers) {
    drawerEntries.push({name: 'Gutscheine', link: VOUCHER_ROUTE, icon: <EuroSymbolIcon/>});
  }
  drawerEntries.push({
    name: 'Veranstaltungen',
    link: SESSION_ROUTE,
    icon: unviewedSessionCount > 0 ? withBadge(<WatchLaterIcon/>, unviewedSessionCount) : <WatchLaterIcon/>,
  });
  drawerEntries.push({name: 'Grußbotschaften', link: GREETING_ROUTE, icon: <ChatBubbleIcon/>});
  if (wins.length > 0) {
    drawerEntries.push({
      name: 'Meine Gewinne',
      link: MY_WINS_ROUTE,
      icon: unclaimedWins.length === 0 ? <StarIcon/> : withBadge(<StarIcon/>, unclaimedWins.length),
    });
  }

  const markContestViewed = () => {
    if (contestIsNew && today) {
      setCookie(LAST_VIEWED_CONTEST_DAY_COOKIE, today.id.toString());
      setLastViewedContestDay(today.id);
    }
  };
  const markSessionsViewed = () => {
    if (unviewedSessionCount > 0) {
      const ids = sessionsOfToday.map(s => s.id);
      setCookie(LAST_VIEWED_SESSION_IDS_COOKIE, JSON.stringify(ids));
      setLastViewedSessionIds(ids);
    }
  };

  return (
    <PageWithNavigation drawerEntries={drawerEntries}>
      <Suspense fallback={<CenteredCircularProgress/>}>
        <Routes>
          <Route path={NEWS_ROUTE} element={<NewsPage/>}/>
          <Route path={CONTEST_ROUTE} element={<Viewed onViewed={markContestViewed}><ContestPage/></Viewed>}/>
          <Route path={VOUCHER_ROUTE} element={<VoucherPage/>}/>
          <Route path={SESSION_ROUTE} element={<Viewed onViewed={markSessionsViewed}><SessionPage/></Viewed>}/>
          <Route path={GREETING_ROUTE} element={<GreetingPage/>}/>
          <Route path={MY_WINS_ROUTE} element={<MyWinsPage wins={wins} onWinsUpdate={updateWins}/>}/>
          <Route path={CONTEST_PARTICIPATION_ROUTE} element={<ContestParticipationPage onWinsUpdate={updateWins}/>}/>
          <Route path={REGISTRATION_ROUTE} element={<RegistrationPage/>}/>
          <Route path={PROFILE_ROUTE} element={<ProfilePage wins={wins} onWinsUpdate={updateWins}/>}/>
          <Route path={PRIVACY_POLICY_ROUTE} element={<PrivacyPolicyPage/>}/>
          <Route path={IMPRINT_ROUTE} element={<ImprintPage/>}/>
          <Route path={AGB_ROUTE} element={<AgbPage/>}/>
          <Route path="/" element={<Navigate to={NEWS_ROUTE} replace={true}/>}/>
          <Route path="*" element={<NotFoundPage/>}/>
        </Routes>
      </Suspense>
    </PageWithNavigation>
  );
}
